import type { AutocompleteProps } from '@mui/material';
import type { BaseAutocompleteInputProps } from '../Autocomplete.types.ts';
import type { BookingChannel } from './BookingChannelsAutocomplete.types.ts';

import { Autocomplete, TextField } from '@mui/material';

import { matchSorter } from '#lib/match-sorter.ts';
import { getTextFieldProps } from '../Autocomplete.utils.ts';
import { bookingOptions, getOptionLabel } from './BookingChannelsAutocomplete.utils.ts';

interface BookingChannelsAutocompleteProps<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
>
  extends
    Omit<
      AutocompleteProps<BookingChannel, Multiple, DisableClearable, false>,
      | 'loading'
      | 'options'
      | 'getOptionLabel'
      | 'filterOptions'
      | 'isOptionEqualToValue'
      | 'onInputChange'
      | 'renderInput'
      | 'freeSolo'
    >,
    BaseAutocompleteInputProps<BookingChannel> {}

// Component renamed in insights
export function BookingChannelsAutocomplete<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
>({
  inputName = 'location',
  inputLabel = 'Location',
  inputHelperText,
  inputError,
  inputRequired,
  inputSize = 'small',
  fullWidth = true,
  filterSelectedOptions = true,
  clearOnBlur = false,
  selectOnFocus = true,
  handleHomeEndKeys = true,
  multiple,
  ...props
}: BookingChannelsAutocompleteProps<Multiple, DisableClearable>): JSX.Element {
  return (
    <Autocomplete
      freeSolo={false}
      multiple={multiple}
      fullWidth={fullWidth}
      clearOnBlur={clearOnBlur}
      selectOnFocus={selectOnFocus}
      handleHomeEndKeys={handleHomeEndKeys}
      loading={false}
      options={bookingOptions}
      getOptionLabel={getOptionLabel}
      filterSelectedOptions={filterSelectedOptions}
      filterOptions={(options, { inputValue }) =>
        matchSorter(options, inputValue, { keys: ['label'] })
      }
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {getOptionLabel(option)}
        </li>
      )}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <TextField
          {...getTextFieldProps({ params, multiple, inputRequired })}
          name={inputName}
          label={inputLabel}
          helperText={inputHelperText}
          error={inputError}
          size={inputSize}
        />
      )}
      {...props}
    />
  );
}
